import { Box, Typography, Paper } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

// Helper function to calculate standard Y-axis configuration
function getStandardYAxisConfig(maxValue: number) {
  const standardUnits = [1, 5, 10, 50, 100, 500, 1000, 5000, 10000];

  // Handle the case where all values are 0
  if (maxValue === 0) {
    return {
      domain: [0, 5],
      interval: 1,
      ticks: [0, 1, 2, 3, 4, 5],
    };
  }

  // Find the appropriate interval and max
  let interval = 1;
  const tickCount = 5; // Target around 5-6 ticks

  for (const unit of standardUnits) {
    const potentialTickCount = Math.ceil(maxValue / unit);
    if (potentialTickCount <= tickCount) {
      interval = unit;
      break;
    }
  }

  // Calculate the ceiling max that's a multiple of the interval
  const adjustedMax = Math.ceil(maxValue / interval) * interval;

  return {
    domain: [0, adjustedMax],
    interval: interval,
    ticks: Array.from({ length: Math.floor(adjustedMax / interval) + 1 }, (_, i) => i * interval),
  };
}

interface RepertoireProgressChartProps {
  data: Array<{ week: string; cumulative_count: number }>;
}

export function RepertoireProgressChart({ data }: RepertoireProgressChartProps) {
  // Transform data for the chart, taking the last 12 weeks
  const chartData = data.slice(-12).map((item) => ({
    date: new Date(item.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count: item.cumulative_count,
    fullDate: item.week,
  }));

  // Get the current total
  const currentTotal = data.length > 0 ? data[data.length - 1].cumulative_count : 0;

  // Calculate Y-axis configuration with standard units
  const maxCount = Math.max(...chartData.map((d) => d.count));
  const yAxisConfig = getStandardYAxisConfig(maxCount);

  return (
    <Paper
      sx={{
        backgroundColor: '#1f2937',
        color: '#ffffff',
        borderRadius: 2,
        padding: 3,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        boxShadow: 'none',
      }}
    >
      <Box sx={{ marginBottom: 2 }}>
        <Typography
          sx={{
            color: '#9ca3af',
            fontSize: '0.875rem',
            marginBottom: '4px',
            fontWeight: 400,
          }}
        >
          Repertoire Completed
        </Typography>
        <Typography
          sx={{
            color: '#ffffff',
            fontSize: '1.5rem',
            fontWeight: 400,
          }}
        >
          {currentTotal.toLocaleString()}
        </Typography>
      </Box>

      <Box sx={{ height: '140px', flex: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: -30, bottom: -5 }}>
            <XAxis
              dataKey="date"
              axisLine={{ stroke: '#374151', strokeWidth: 1 }}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              orientation="bottom"
            />
            <YAxis
              axisLine={{ stroke: '#374151', strokeWidth: 1 }}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              domain={yAxisConfig.domain}
              ticks={yAxisConfig.ticks}
              orientation="left"
            />
            <Tooltip
              isAnimationActive={false}
              contentStyle={{
                backgroundColor: '#374151',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              }}
              formatter={(value: number) => [value.toLocaleString()]}
              labelFormatter={() => ''}
            />
            {data.length > 0 && (
              <Line
                type="monotone"
                dataKey="count"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ fill: '#22c55e', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, stroke: '#22c55e', strokeWidth: 2, fill: '#22c55e' }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
